/**
 * V2 canvas layout tokens — the centred body column and 24px canvas-edge safe
 * area shared by every V2 tab.
 *
 * One constant per DOM shape, each a complete literal string: Tailwind's JIT
 * scanner only emits class names it can see in source, so `'max-w-' + token`
 * or `` `[&_.ProseMirror]:${WIDTH}` `` would never reach the CSS bundle.
 *
 * The column tokens are NOT interchangeable — what draws the *text* differs
 * per tab, and below `sm` they inset differently:
 *
 *  - Note / Document — `V2_CANVAS_PROSEMIRROR_CLASSNAME`. Plain siblings that
 *    must line up under it (the CSAT footers) use
 *    `V2_CANVAS_PROSEMIRROR_COLUMN_CLASSNAME`; those two are a pair.
 *  - Transcript / Context — no ProseMirror token in play; their editors strip
 *    their own horizontal padding, so `V2_CANVAS_BODY_CLASSNAME` *is* the text
 *    column there.
 *  - Note JSON list — same again via `V2_CANVAS_DIRECT_CHILDREN_CLASSNAME`.
 *
 * So widening the ProseMirror pair does not imply widening the other two —
 * that moves Transcript and Context body copy, not a trailing affordance.
 */

/**
 * Wrapper for the canvas body column, used inside `V2CanvasContentFrame`.
 *
 * Carries no vertical padding: that lives in `V2_CANVAS_BODY_TOP_INSET` /
 * `V2_CANVAS_BODY_BOTTOM_INSET` and applies only to the scrollable body slot,
 * so toolbars sit flush at the top of the canvas.
 */
export const V2_CANVAS_BODY_CLASSNAME = 'mx-auto w-full max-w-2xl px-6';

/**
 * Full-width twin of {@link V2_CANVAS_BODY_CLASSNAME}, selected by the
 * "Full width" canvas preference (SCR-7399). Kept as a complete literal
 * alongside its base for the same JIT reason documented in the module header:
 * `max-w-` cannot be interpolated, and appending `max-w-7xl` after `max-w-2xl`
 * would not reliably win, so full width means *swapping* to this string via
 * {@link bodyColumnClass}.
 */
export const V2_CANVAS_BODY_CLASSNAME_FULL = 'mx-auto w-full max-w-7xl px-6';

/**
 * For plain wrappers that must line up under a `.ProseMirror` text column on
 * the same tab — today the Note and Document CSAT footers.
 *
 * Keep its padding equal to `V2_CANVAS_PROSEMIRROR_CLASSNAME`'s, and do not
 * fold either into `V2_CANVAS_BODY_CLASSNAME` (see the module header).
 */
export const V2_CANVAS_PROSEMIRROR_COLUMN_CLASSNAME =
  'mx-auto w-full max-w-2xl sm:px-6 px-1';

/** Full-width twin of {@link V2_CANVAS_PROSEMIRROR_COLUMN_CLASSNAME} (SCR-7399). */
export const V2_CANVAS_PROSEMIRROR_COLUMN_CLASSNAME_FULL =
  'mx-auto w-full max-w-7xl sm:px-6 px-1';

/**
 * Top safe area for the scrollable body slot of `V2CanvasContentFrame`,
 * applied only when the frame has no top-bar — a top-bar's own padding already
 * provides the canvas-top safe area, and stacking both leaves a 30–40px gap.
 *
 * Desktop-only: on mobile the canvas content wrapper (`V2MainCanvas` /
 * `V2WindowPane`) already applies its own `pt-4`, and a second 24px on top
 * pushed the body well below the controls. The gutter inset supersedes it
 * when the frame renders an action gutter, has no top-bar, and owns the
 * scroller (`bodyFillsHeight` off).
 */
export const V2_CANVAS_BODY_TOP_INSET = 'sm:pt-6';

/** Bottom safe area for the scrollable body slot. Always applied. */
export const V2_CANVAS_BODY_BOTTOM_INSET = 'pb-6';

/**
 * Gap above the CSAT thumbs where they flow at the end of a tab's content
 * (Note, Transcript, Document) rather than sitting in a toolbar. 12px rather
 * than the canvas's usual 24 because `BinaryFeedbackButton` already carries
 * ~6px of slack above the glyph, so it reads as ~26px.
 *
 * Un-prefixed on purpose, and the only export here you apply ungated: the
 * mobile and V1 note/document editors flow the same prose under the same
 * bare-sibling footer and need the same gap. Only the `max-w-2xl` centring
 * applied alongside it is V2-gated.
 */
export const INLINE_FEEDBACK_TOP_INSET = 'mt-3';

/**
 * Centres and insets the `.ProseMirror` contenteditable nested anywhere inside
 * the wrapper. The wrapper itself stays full-width so the scroll wheel
 * captures over the whole canvas. Used by Note + Document tabs (both v1 and
 * markdown editors).
 */
export const V2_CANVAS_PROSEMIRROR_CLASSNAME =
  '[&_.ProseMirror]:mx-auto [&_.ProseMirror]:w-full [&_.ProseMirror]:max-w-2xl [&_.ProseMirror]:sm:px-6 [&_.ProseMirror]:px-1';

/** Full-width twin of {@link V2_CANVAS_PROSEMIRROR_CLASSNAME} (SCR-7399). */
export const V2_CANVAS_PROSEMIRROR_CLASSNAME_FULL =
  '[&_.ProseMirror]:mx-auto [&_.ProseMirror]:w-full [&_.ProseMirror]:max-w-7xl [&_.ProseMirror]:sm:px-6 [&_.ProseMirror]:px-1';

/**
 * Bottom safe area inside the editor's `.prose-mirror-container` scroll
 * element (shared by the v1, vMarkdown and markdown-native editors). It sits
 * on the `overflow-y-auto` element itself, so it scrolls with the content and
 * only appears once the last line does — keeping the end of the note (or the
 * selected-codes block) clear of the Ask-Heidi composer sibling below.
 */
export const V2_CANVAS_PROSEMIRROR_BOTTOM_INSET = '[&_.prose-mirror-container]:pb-6';

/**
 * Top safe area INSIDE the Note / Document / `V2CanvasContentFrame` scroll
 * container so first-row content clears the floating action gutter
 * (`V2CanvasActionGutter`) at scrollTop 0, while scrolled content still
 * runs under the gutter and is clipped at the canvas top edge, never at
 * the gutter's box. The frame applies it in place of
 * `V2_CANVAS_BODY_TOP_INSET` when it renders an action gutter, has no
 * top-bar, and owns the scroller (`bodyFillsHeight` off). 56px = gutter
 * top (`top-6` − `-mt-1`) + the xs button row (h-7) + 8px clearance; the
 * same value in both column widths because the full-width column runs
 * under the gutter horizontally. Lives on the scroller (padding inside the
 * scrollport), NOT on the positioned ancestor — padding outside the
 * scroller moves the clip line down and produces a full-width cut.
 * Gate on the gutter's own render condition so nothing pads when there is
 * no gutter. Note and Document both hide the gutter (and this inset) with
 * their overlays, and drop it when a focused Work pane hides the gutter.
 * The Note tab also drops it while an in-flow row above the editor (dictation
 * playback, the poor-audio warning) is rendered — that row is the clearance
 * then. V1 and mobile have no gutter.
 */
export const V2_CANVAS_GUTTER_TOP_INSET = 'pt-14';

/** `.prose-mirror-container` twin of {@link V2_CANVAS_GUTTER_TOP_INSET}, for wrappers that ride `editorWrapperClassName` like `V2_CANVAS_PROSEMIRROR_BOTTOM_INSET`. */
export const V2_CANVAS_PROSEMIRROR_GUTTER_TOP_INSET = '[&_.prose-mirror-container]:pt-14';

/**
 * Top padding for in-flow isolate siblings that share a `relative isolate`
 * with `V2CanvasActionGutter` (`absolute end-6 top-6`) but are not a
 * scroller. PDF / XML / Prechart content sits in normal flow, so they only
 * need to match the gutter's `top-6` — unlike {@link V2_CANVAS_GUTTER_TOP_INSET}
 * (`pt-14`), which is inside a scroller whose first row must clear the
 * floating cluster as the user scrolls.
 */
export const V2_CANVAS_GUTTER_ALIGN_TOP_INSET = 'pt-6';

/**
 * V2 editor shells (markdown-native shell, `BlockEditorV1Container`,
 * `JSONBlockEditorContainer`) drop the V1 `sm:pt-4` + `gap-3` chrome above
 * the scroller so the scroll container starts at the canvas top. The top
 * safe area is provided inside the scroller by the gutter top-inset pair.
 */
export const V2_CANVAS_EDITOR_SHELL_FLUSH_CLASSNAME = 'gap-0 sm:pt-0';

/**
 * Centres and insets every immediate child, leaving the wrapper full-width for
 * the same scroll-capture reason as `V2_CANVAS_PROSEMIRROR_CLASSNAME`. Used by
 * the Note tab JSON editor list.
 */
export const V2_CANVAS_DIRECT_CHILDREN_CLASSNAME =
  '[&>*]:mx-auto [&>*]:w-full [&>*]:max-w-2xl [&>*]:px-6';

/** Full-width twin of {@link V2_CANVAS_DIRECT_CHILDREN_CLASSNAME} (SCR-7399). */
export const V2_CANVAS_DIRECT_CHILDREN_CLASSNAME_FULL =
  '[&>*]:mx-auto [&>*]:w-full [&>*]:max-w-7xl [&>*]:px-6';

/**
 * "Full width" preference selectors (SCR-7399) — map the boolean preference to
 * the right complete literal for each canvas DOM shape. Callers read the
 * preference with `useIsCanvasFullWidth()` and pass it here instead of importing
 * a static width constant, so widening every canvas tab is one swap per shape
 * rather than a scatter of inline ternaries. Kept beside the constants they
 * choose between so the base/full pair can't drift.
 */
export const bodyColumnClass = (fullWidth: boolean): string =>
  fullWidth ? V2_CANVAS_BODY_CLASSNAME_FULL : V2_CANVAS_BODY_CLASSNAME;

export const proseMirrorColumnClass = (fullWidth: boolean): string =>
  fullWidth
    ? V2_CANVAS_PROSEMIRROR_COLUMN_CLASSNAME_FULL
    : V2_CANVAS_PROSEMIRROR_COLUMN_CLASSNAME;

export const proseMirrorClass = (fullWidth: boolean): string =>
  fullWidth ? V2_CANVAS_PROSEMIRROR_CLASSNAME_FULL : V2_CANVAS_PROSEMIRROR_CLASSNAME;

export const directChildrenColumnClass = (fullWidth: boolean): string =>
  fullWidth
    ? V2_CANVAS_DIRECT_CHILDREN_CLASSNAME_FULL
    : V2_CANVAS_DIRECT_CHILDREN_CLASSNAME;

/**
 * The floating top-right action cluster owned by `V2CanvasActionGutter` (Copy
 * on Transcript, VersionController on Note / Context / Document, etc.).
 *
 * Position offsets rather than padding, so the absolutely-pinned box sizes
 * exactly to its children — padding would leave unused click area invisibly
 * blocking the body content underneath. `end-6 top-6` mirrors the body's
 * canvas-edge inset; keep the two in step.
 *
 * The `-mt-1` compensates for a small downward offset that every tab's
 * wrapper chain (Context's `<section>`, Note + Document's editor wrappers,
 * Transcript's `TranscriptOuterChrome`) adds, which leaves the raw `top-6`
 * ~4px too low. It is here rather than per-tab because all four tabs need it.
 */
export const V2_CANVAS_ACTION_GUTTER_CLASSNAME = 'absolute end-6 top-6 z-10 -mt-1';

/**
 * In-flow twin of {@link V2_CANVAS_ACTION_GUTTER_CLASSNAME} for the one
 * toolbar row V2 keeps in flow (the Note tab's dictation playback row): the
 * control's top edge and canvas-edge inset match the floating gutter's.
 *
 * Top: `pt-5` = the gutter's `top-6` less its `-mt-1` (20px); keep the two
 * in step.
 *
 * Left: the editor shells wrap the toolbar slot in `sm:px-4` (16px at ≥sm,
 * flush below), so the row adds `sm:px-2` to land on the gutter's 24px
 * `end-6`, and `px-6` below `sm` where the wrapper is flush.
 *
 * The row's height is `pt-5` (20px) + the xs control (`h-7`, 28px — the same
 * design-system xs height as the gutter cluster) + `pb-2` (8px, the same
 * clearance the gutter inset gives text under the cluster) = 56px =
 * {@link V2_CANVAS_GUTTER_TOP_INSET}, so callers drop that scroller inset
 * while this row is rendered (see NoteTabPane). The swap is only sound
 * while the control itself is in the row: an empty row is only the padding
 * and would leave the first line under the gutter.
 */
export const V2_CANVAS_TOOLBAR_ROW_INSET_CLASSNAME = 'px-6 pb-2 pt-5 sm:px-2';

/**
 * Surface for wrappers nested INSIDE the V2 canvas.
 *
 * The canvas card (`V2MainCanvas`) paints `bg-surface-shell`. Shared TabPane
 * wrappers paint an opaque `bg-surface` of their own, so they must paint the
 * same shell token in V2 or they show as rectangles on the canvas whenever
 * `surface` and `surface-shell` differ. Pass this after their existing
 * `bg-surface` in `cn` (tailwind-merge drops the base), gated on
 * `useIsV2Layout()`: V1's outer chrome still paints bare `bg-surface`.
 */
export const V2_CANVAS_SURFACE = 'bg-surface-shell';

/**
 * Gradient-stop counterpart of `V2_CANVAS_SURFACE`, for fades that dissolve
 * into the canvas (pinned overlays, scroll fades) rather than painting it
 * flat. Same `cn` ordering and `useIsV2Layout()` gate.
 *
 * End the fade on `to-surface-shell/0` rather than `to-transparent`, as
 * `ProjectChatScrollFades` does. Evergreen engines interpolate gradients in
 * premultiplied alpha so both render the same today, but `transparent` is
 * `rgba(0,0,0,0)` and would darken the midpoint if that ever changed.
 */
export const V2_CANVAS_FADE = 'from-surface-shell to-surface-shell/0';

/**
 * Backdrop for the in-flow Ask Heidi strip when the canvas is showing the
 * interactive PDF form preview — the one tab whose body isn't flat white, so
 * the card's `bg-surface-shell` cuts a hard line where the form's sand document
 * area ends. Same token that document area uses, so the two can't drift.
 *
 * Needs no `newBrandDark:` companion: both surfaces resolve to black there.
 */
export const V2_CANVAS_FORM_SURFACE = 'bg-surface-page';
